import React, { useMemo } from "react";
import { Button } from "@shared/components";
import { changeListeners, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { Goal } from "@shared/models";
import { CreateGoalDto } from "@containers/Goal/interfaces";

import { getInitValues, validationSchema, prepareSubmitValue } from "./formHelpers";

import "./index.scss";

interface GoalFormProps {
  goal: Goal | null;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateGoalDto) => void;
}

const GoalFormFields: React.FunctionComponent<GoalFormProps> = (props) => {
  const { goal, onChangeForm, onCloseForm, onSubmitForm } = props;

  const formValues = useMemo(() => getInitValues(goal), [goal]);

  const form = useAppForm({
    defaultValues: formValues,
    validators: schemaValidators(validationSchema),
    listeners: changeListeners(onChangeForm),
    onSubmit: ({ value }) => {
      onSubmitForm(prepareSubmitValue(value));
    },
  });

  return (
    <div className="goal-form">
      <form onSubmit={formSubmitHandler(form)}>
        <div className="goal-form-content">
          <div className="input-wrapper">
            <form.AppField name="name">
              {(field) => (
                <field.Input type="text" className="text-input" labelTitle="Goal Objective" placeholder="Name" />
              )}
            </form.AppField>
          </div>
          <div className="input-wrapper">
            <form.AppField name="due_date">
              {(field) => (
                <field.Input type="date" className="date-input" labelTitle="Due Date" placeholder="Start Date" />
              )}
            </form.AppField>
          </div>
          <div className="input-wrapper">
            <form.AppField name="description">
              {(field) => (
                <field.Textarea
                  type="text"
                  className="textarea-input"
                  labelTitle="Short Description"
                  placeholder="Description"
                />
              )}
            </form.AppField>
          </div>
          <div className="input-wrapper goal-progress-input">
            <form.AppField name="progress">
              {(field) => (
                <field.Input
                  type="number"
                  className="text-input number"
                  labelTitle="Progress"
                  placeholder="progress"
                  min={0}
                  max={100}
                  additionalSymbol="%"
                />
              )}
            </form.AppField>
          </div>
        </div>
        <div className="goal-form-footer">
          <Button type="button" className="close-btn margin-right" onClick={onCloseForm}>
            Cancel
          </Button>
          <Button type="submit" className="accent padding">
            {goal ? "Save" : "Save Goal"}
          </Button>
        </div>
      </form>
    </div>
  );
};

/** Keyed on the goal's initial values: a goal that arrives or changes while the form is open mounts a fresh form. */
const GoalForm: React.FunctionComponent<GoalFormProps> = (props) => (
  <GoalFormFields key={JSON.stringify(getInitValues(props.goal))} {...props} />
);

export default GoalForm;
